import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import ThemeToggle from '../components/ThemeToggle';
import ShareButton from '../components/ShareButton';

// Lecteur de QCM plein écran, pensé pour le mobile (inspiré des jeux mobiles) :
// accueil → une question par écran (Suivant, ou Vérifier puis Continuer) → score animé + correction.

const LETTERS = 'ABCDEFGHIJ';
const fmt = (ms) => {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
};
const vibrate = (p) => { try { if (navigator.vibrate) navigator.vibrate(p); } catch {} };

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" /></svg>
);
const Check = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" /></svg>
);
const Flame = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2c1 4-3 5-3 9a3 3 0 0 0 6 0c0-1-.5-2-1-3 3 1 5 4 5 7a7 7 0 0 1-14 0c0-5 4-7 7-13Z" fill="currentColor" /></svg>
);

function Confetti() {
  const pieces = useMemo(() => Array.from({ length: 46 }, (_, i) => ({
    left: Math.random() * 100,
    delay: Math.random() * 0.8,
    dur: 2.4 + Math.random() * 1.6,
    rot: Math.random() * 360,
    color: ['#f2b544', '#a3244a', '#c0436a', '#5b6b3a', '#e6b8c4', '#3fa34d'][i % 6],
    w: 6 + Math.random() * 6,
  })), []);
  return (
    <div className="g-confetti" aria-hidden="true">
      {pieces.map((p, i) => (
        <span key={i} style={{ left: `${p.left}%`, animationDelay: `${p.delay}s`, animationDuration: `${p.dur}s`, background: p.color, width: p.w, height: p.w * 1.6, transform: `rotate(${p.rot}deg)` }} />
      ))}
    </div>
  );
}

function ScoreRing({ pct }) {
  const r = 70, c = 2 * Math.PI * r;
  return (
    <svg className="g-ring" viewBox="0 0 180 180" aria-hidden="true">
      <circle cx="90" cy="90" r={r} className="g-ring-track" />
      <circle cx="90" cy="90" r={r} className="g-ring-fill" style={{ strokeDasharray: c, '--ring-off': c * (1 - pct / 100), '--ring-full': c }} />
    </svg>
  );
}

export default function QcmPlay() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [quiz, setQuiz] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [phase, setPhase] = useState('intro'); // intro | playing | result
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});  // { questionId: [indices] }
  const [checks, setChecks] = useState({});    // mode immédiat : { questionId: { correct, correct_options, explanation } }
  const [checking, setChecking] = useState(false);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [endAt, setEndAt] = useState(null);
  const [startedAt, setStartedAt] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');           // message affiché
  const [errorKind, setErrorKind] = useState(null);  // 'submit' | 'check'
  const [timedOut, setTimedOut] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const submittedRef = useRef(false);
  const answersRef = useRef(answers);
  answersRef.current = answers;

  useEffect(() => {
    fetch(`/api/qcm/quizzes/${id}`)
      .then(r => { if (!r.ok) throw new Error(r.status === 404 ? 'Ce QCM n\'existe pas ou n\'est plus publié.' : 'Chargement impossible.'); return r.json(); })
      .then(setQuiz)
      .catch(e => setLoadError(e.message));
  }, [id]);

  const immediate = quiz && quiz.feedback_mode === 'immediate';
  const total = quiz ? quiz.questions.length : 0;
  const subjectLink = quiz && quiz.subject_id ? `/qcm/sujet/${quiz.subject_id}` : '/qcm';

  const submit = useCallback(async () => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    setSubmitting(true); setError(''); setErrorKind(null);
    try {
      const r = await fetch(`/api/qcm/quizzes/${id}/submit`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers: answersRef.current })
      });
      if (!r.ok) throw new Error();
      setResult({ ...(await r.json()), duration: Date.now() - startedAt });
      setPhase('result');
      window.scrollTo({ top: 0 });
    } catch {
      submittedRef.current = false;
      setError('Connexion perdue : impossible d\'envoyer tes réponses. Réessaie.');
      setErrorKind('submit');
    } finally {
      setSubmitting(false);
    }
  }, [id, startedAt]);

  // Timer : à zéro, le QCM est validé automatiquement
  useEffect(() => {
    if (phase !== 'playing' || !endAt) return;
    const t = setInterval(() => {
      const n = Date.now();
      setNow(n);
      if (n >= endAt) { clearInterval(t); setTimedOut(true); submit(); }
    }, 250);
    return () => clearInterval(t);
  }, [phase, endAt, submit]);

  useEffect(() => {
    if (phase !== 'playing') return;
    const h = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [phase]);

  const start = () => {
    const t = Date.now();
    submittedRef.current = false;
    setAnswers({}); setChecks({}); setResult(null); setTimedOut(false); setError(''); setErrorKind(null);
    setStreak(0); setBestStreak(0); setShowReview(false); setIdx(0);
    setStartedAt(t); setNow(t);
    setEndAt(quiz.timer_enabled ? t + quiz.timer_minutes * 60000 : null);
    setPhase('playing');
    window.scrollTo({ top: 0 });
  };

  const q = quiz && phase === 'playing' ? quiz.questions[idx] : null;
  const selected = q ? (answers[q.id] || []) : [];
  const check = q ? checks[q.id] : null;
  const isLast = idx === total - 1;

  const select = (j) => {
    if (!q || check || checking || submitting || timedOut) return;
    setAnswers(prev => {
      const cur = prev[q.id] || [];
      const next = q.multiple ? (cur.includes(j) ? cur.filter(i => i !== j) : [...cur, j].sort()) : [j];
      return { ...prev, [q.id]: next };
    });
  };

  const goNext = () => { setIdx(i => i + 1); window.scrollTo({ top: 0 }); };

  const verify = async () => {
    setChecking(true); setError(''); setErrorKind(null);
    try {
      const r = await fetch(`/api/qcm/quizzes/${id}/check`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question_id: q.id, answer: selected })
      });
      if (!r.ok) throw new Error();
      const d = await r.json();
      setChecks(prev => ({ ...prev, [q.id]: d }));
      if (d.correct) {
        setStreak(s => { const n = s + 1; setBestStreak(b => Math.max(b, n)); return n; });
      } else {
        setStreak(0);
        vibrate(120);
      }
    } catch {
      setError('Connexion perdue : impossible de vérifier. Réessaie.');
      setErrorKind('check');
    } finally {
      setChecking(false);
    }
  };

  const primary = () => {
    if (!q || submitting || checking) return;
    if (immediate && !check) { if (selected.length) verify(); return; }
    if (!immediate && !selected.length) return;
    if (isLast) submit(); else goNext();
  };

  // Clavier (ordinateur) : 1-9 ou A-J pour choisir, Entrée pour valider
  useEffect(() => {
    if (phase !== 'playing') return;
    const onKey = (e) => {
      if (e.key === 'Enter') { e.preventDefault(); primary(); return; }
      const k = e.key.toUpperCase();
      const n = /^[1-9]$/.test(k) ? Number(k) - 1 : LETTERS.indexOf(k);
      if (q && n >= 0 && n < q.options.length) select(n);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const quit = () => {
    if (phase === 'playing' && !window.confirm('Quitter le QCM ? Ta progression sera perdue.')) return;
    navigate(subjectLink);
  };

  // ------------------------------------------------------------------
  if (loadError) {
    return (
      <div className="g-shell"><div className="g-center">
        <p className="g-error-big">{loadError}</p>
        <Link to="/qcm" className="g-btn">tous les sujets</Link>
      </div></div>
    );
  }
  if (!quiz) return <div className="g-shell"><div className="g-center"><div className="g-spinner" aria-label="chargement" /></div></div>;

  // ---------- Accueil ----------
  if (phase === 'intro') {
    return (
      <div className="g-shell">
        <header className="g-top">
          <button type="button" className="g-icon-btn" onClick={quit} aria-label="Retour au sujet"><CloseIcon /></button>
          <span className="g-top-title">{quiz.subject_name}</span>
          <ThemeToggle />
        </header>
        <main className="g-center g-intro">
          <div className="g-intro-badge" aria-hidden="true"><span>?</span><i /><i /><i /></div>
          <h1 className="g-intro-title">{quiz.title}</h1>
          {quiz.description && <p className="g-intro-desc">{quiz.description}</p>}
          <div className="g-chips">
            <span className="g-chip">{total} question{total > 1 ? 's' : ''}</span>
            <span className={`g-chip ${quiz.timer_enabled ? 'hot' : ''}`}>{quiz.timer_enabled ? `${quiz.timer_minutes} min` : 'sans timer'}</span>
            <span className="g-chip">{immediate ? 'correction à chaque question' : 'correction à la fin'}</span>
          </div>
          <div className="g-intro-cta">
            <button type="button" className="g-btn g-btn-xl" onClick={start} disabled={total === 0}>Jouer</button>
            <ShareButton url={`/qcm/${quiz.id}`} variant="link" className="g-share-link" label="défier un ami"
              title={`QCM : ${quiz.title}`} text={`Je te défie sur ce QCM « ${quiz.title} » (${total} question${total > 1 ? 's' : ''}) 🎯`} />
          </div>
        </main>
      </div>
    );
  }

  // ---------- Résultat ----------
  if (phase === 'result') {
    const pct = result.total ? Math.round((result.score / result.total) * 100) : 0;
    const title = pct === 100 ? 'Parfait !' : pct >= 70 ? 'Bravo !' : pct >= 40 ? 'Pas mal !' : 'On s\'accroche !';
    const sub = pct === 100 ? 'Sans aucune faute.' : pct >= 70 ? 'Tu maîtrises bien le sujet.' : pct >= 40 ? 'Encore un petit effort.' : 'Regarde la correction et retente ta chance.';
    const byId = Object.fromEntries(result.results.map(r => [r.question_id, r]));
    return (
      <div className="g-shell">
        {pct >= 70 && <Confetti />}
        <header className="g-top">
          <button type="button" className="g-icon-btn" onClick={quit} aria-label="Retour au sujet"><CloseIcon /></button>
          <span className="g-top-title">{quiz.title}</span>
          <ThemeToggle />
        </header>
        <main className="g-center g-result">
          {timedOut && <div className="g-timeout">Temps écoulé</div>}
          <div className="g-ring-wrap">
            <ScoreRing pct={pct} />
            <div className="g-ring-label"><strong>{result.score}</strong><span>/{result.total}</span></div>
          </div>
          <h1 className="g-result-title">{title}</h1>
          <p className="g-result-sub">{sub}</p>
          <div className="g-stats">
            <div><strong>{pct}%</strong><span>réussite</span></div>
            <div><strong>{fmt(result.duration)}</strong><span>temps</span></div>
            {immediate && <div><strong>{bestStreak}</strong><span>meilleure série</span></div>}
          </div>
          <div className="g-result-actions">
            <ShareButton url={`/qcm/${quiz.id}`} variant="game" className="g-btn" label="partager mon score"
              title={`QCM : ${quiz.title}`} text={`J'ai eu ${result.score}/${result.total} (${pct} %) au QCM « ${quiz.title} » 🎯 À toi de jouer !`} />
            <button type="button" className="g-btn g-btn-soft" onClick={start}>Rejouer</button>
            <button type="button" className="g-btn g-btn-soft" onClick={() => setShowReview(s => !s)} aria-expanded={showReview}>
              {showReview ? 'Masquer la correction' : 'Voir la correction'}
            </button>
            <Link to={subjectLink} className="g-link">autres QCM du sujet</Link>
          </div>

          {showReview && (
            <div className="g-review">
              {quiz.questions.map((x, i) => {
                const r = byId[x.id] || { correct: false, given: [], correct_options: [], explanation: '' };
                return (
                  <div key={x.id} className={`g-review-item ${r.correct ? 'ok' : 'ko'}`}>
                    <div className="g-review-head"><span>Question {i + 1}</span><b>{r.correct ? 'juste' : 'faux'}</b></div>
                    <p className="g-review-q">{x.text}</p>
                    <ul>
                      {x.options.map((o, j) => {
                        const good = r.correct_options.includes(j), mine = r.given.includes(j);
                        return (
                          <li key={j} className={good ? 'good' : mine ? 'bad' : ''}>
                            <span className="g-letter">{LETTERS[j]}</span>{o}{mine && <em>ta réponse</em>}
                          </li>
                        );
                      })}
                    </ul>
                    {r.given.length === 0 && <p className="g-review-note">pas de réponse</p>}
                    {r.explanation && <p className="g-review-expl">{r.explanation}</p>}
                  </div>
                );
              })}
            </div>
          )}
        </main>
      </div>
    );
  }

  // ---------- Jeu ----------
  const remaining = endAt ? endAt - now : null;
  const progress = ((idx + (check || (!immediate && selected.length) ? 1 : 0)) / total) * 100;
  const canAct = immediate ? (check ? true : selected.length > 0) : selected.length > 0;
  const btnLabel = submitting ? 'Envoi…' : checking ? 'Vérification…' : errorKind === 'submit' ? 'Réessayer'
    : immediate ? (check ? (isLast ? 'Voir mon score' : 'Continuer') : 'Vérifier')
    : (isLast ? 'Terminer' : 'Suivant');

  return (
    <div className={`g-shell g-playing ${check ? (check.correct ? 'is-ok' : 'is-ko') : ''}`}>
      <header className="g-top">
        <button type="button" className="g-icon-btn" onClick={quit} aria-label="Quitter le QCM"><CloseIcon /></button>
        <div className="g-progress" role="progressbar" aria-valuemin="0" aria-valuemax={total} aria-valuenow={idx + 1}>
          <div style={{ width: `${Math.max(4, progress)}%` }} />
        </div>
        {remaining !== null
          ? <span className={`g-timer ${remaining < 60000 ? 'urgent' : ''}`}>{fmt(remaining)}</span>
          : <span className="g-count">{idx + 1}/{total}</span>}
      </header>

      <main className="g-stage">
        <div key={q.id} className="g-card">
          <div className="g-q-meta">
            <span className="g-q-num">Question {idx + 1} <small>/ {total}</small></span>
            {immediate && streak >= 2 && <span className="g-streak"><Flame />{streak}</span>}
          </div>
          <h2 className="g-q-text">{q.text}</h2>
          {q.multiple ? (
            <div className="g-instr multi" role="note">
              <span className="g-instr-icon"><Check /></span>
              <span><strong>Plusieurs réponses</strong> · choisis-en {q.answer_count}</span>
              <span className={`g-instr-count ${selected.length === q.answer_count ? 'done' : ''}`}>{selected.length}/{q.answer_count}</span>
            </div>
          ) : (
            <div className="g-instr" role="note">Une seule réponse</div>
          )}

          <div className="g-options" role={q.multiple ? 'group' : 'radiogroup'}>
            {q.options.map((opt, j) => {
              const picked = selected.includes(j);
              let state = picked ? 'picked' : '';
              if (check) {
                const good = check.correct_options.includes(j);
                state = good ? 'right' : picked ? 'wrong' : 'dim';
              }
              return (
                <button key={j} type="button" className={`g-option ${state}`} onClick={() => select(j)}
                  role={q.multiple ? 'checkbox' : 'radio'} aria-checked={picked} disabled={!!check || timedOut}
                  style={{ animationDelay: `${0.05 + j * 0.05}s` }}>
                  <span className={`g-letter ${q.multiple ? 'sq' : ''}`}>{LETTERS[j]}</span>
                  <span className="g-option-text">{opt}</span>
                  {q.multiple && <span className={`g-tick ${picked ? 'on' : ''}`}>{picked && <Check />}</span>}
                </button>
              );
            })}
          </div>
        </div>
      </main>

      <footer className={`g-bar ${check ? (check.correct ? 'ok' : 'ko') : ''}`}>
        {check && (
          <div className="g-feedback">
            <p className="g-feedback-title">{check.correct ? 'Bien joué !' : 'Pas tout à fait…'}</p>
            {!check.correct && q.multiple && (
              <p className="g-feedback-hint">Il fallait cocher les {q.answer_count} bonnes réponses.</p>
            )}
            {!check.correct && (
              <p className="g-feedback-answer">
                Bonne réponse : {check.correct_options.map(i => `${LETTERS[i]}. ${q.options[i]}`).join(' · ')}
              </p>
            )}
            {check.explanation && <p className="g-feedback-expl">{check.explanation}</p>}
          </div>
        )}
        {error && <p className="g-error">{error}</p>}
        <button type="button"
          className={`g-btn g-btn-xl ${check ? (check.correct ? 'g-btn-ok' : 'g-btn-ko') : ''}`}
          onClick={errorKind === 'submit' ? submit : primary} disabled={errorKind === 'submit' ? submitting : (!canAct || submitting || checking || timedOut)}>
          {btnLabel}
        </button>
      </footer>
    </div>
  );
}
